import { useState, useEffect, useRef } from 'react';

// A live wall-clock hook for Premium Components that render relative timestamps
// ("3 min ago"). Returns the current epoch milliseconds and re-renders the calling
// component on a fixed interval, so relative-time labels stay accurate as time
// passes instead of freezing at first render. Pair it with `relTime(iso, now)`.
//
// Contract:
//   - First render returns `Date.now()` (the lazy initializer reads it at mount).
//   - Re-renders the caller every `intervalMs` with a fresh `Date.now()`.
//   - Only components that call this hook re-render on tick — a chart-heavy slot
//     that shows no relative times simply never imports it and pays nothing.
//   - Cleans up its interval on unmount; a `mounted` guard drops any late tick.
//
//   const now = useNow();        // default 30s cadence (minute-granularity labels)
//   const now = useNow(1000);    // 1s, e.g. a live countdown
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    const id = setInterval(() => { if (mounted.current) setNow(Date.now()); }, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  return now;
}
